"use client";

import { useEffect, useState } from "react";
import { Share, Plus, X, EllipsisVertical, Compass, ArrowDownToLine } from "lucide-react";
import {
  OPEN_GEBEURTENIS,
  haalPrompt,
  leesToestel,
  magTonen,
  onthoudKlaar,
  onthoudLater,
  vergeetPrompt,
  type Toestel,
} from "@/lib/installeren";

// Een strook onderin die uitlegt hoe je de app op het beginscherm zet.
//
// Niet meteen bij het openen. Wie voor het eerst binnenkomt wil eerst zien wat
// dit is; een venster voor de eerste kalender in beeld staat, is een venster
// dat je wegtikt zonder te lezen. Vandaar de wachttijd hieronder.
const WACHT_MS = 4000;

type Stand = { toestel: Toestel; metKnop: boolean };

export function InstalleerTip() {
  const [stand, setStand] = useState<Stand | null>(null);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    let klok: ReturnType<typeof setTimeout> | undefined;

    function toon(toestel?: Toestel) {
      setStand({ toestel: toestel ?? leesToestel(), metKnop: haalPrompt() !== null });
    }

    // Vanuit "Meer", waar de rij staat die deze strook terughaalt.
    function opVerzoek() {
      toon();
    }

    // Chrome kan de installatiegebeurtenis ook ná ons in beeld komen afvuren.
    // Dan wisselen we alsnog van aanwijzingen naar een echte knop.
    function opPrompt() {
      setStand((s) => (s ? { ...s, metKnop: true } : s));
    }

    // Iemand zet hem alsnog vanuit het browsermenu op het beginscherm. Dan is
    // deze strook klaar, voorgoed.
    function opGeinstalleerd() {
      onthoudKlaar();
      vergeetPrompt();
      setStand(null);
    }

    window.addEventListener(OPEN_GEBEURTENIS, opVerzoek);
    window.addEventListener("beforeinstallprompt", opPrompt);
    window.addEventListener("appinstalled", opGeinstalleerd);

    // `?installeren` haalt de strook meteen tevoorschijn, ook als hij al eens
    // is weggetikt. Voor het nakijken op een echt toestel, en om hem tijdens een
    // demo te laten zien zonder eerst opslag te wissen. Met `?installeren=ios`
    // of `=android` kies je welke variant je wilt zien - anders krijg je die van
    // het toestel waar je op zit.
    const vraag = new URLSearchParams(window.location.search);
    const geforceerd = vraag.get("installeren");

    if (geforceerd !== null) {
      const keuze: Record<string, Toestel> = {
        ios: "ios-safari",
        "ios-anders": "ios-anders",
        android: "android",
      };
      const eigen = leesToestel();
      klok = setTimeout(
        () => toon(keuze[geforceerd] ?? (eigen === "bureaublad" ? "ios-safari" : eigen)),
        0,
      );
    } else if (magTonen()) {
      klok = setTimeout(toon, WACHT_MS);
    }

    return () => {
      if (klok) clearTimeout(klok);
      window.removeEventListener(OPEN_GEBEURTENIS, opVerzoek);
      window.removeEventListener("beforeinstallprompt", opPrompt);
      window.removeEventListener("appinstalled", opGeinstalleerd);
    };
  }, []);

  if (!stand) return null;

  async function installeer() {
    const prompt = haalPrompt();
    if (!prompt) return;
    setBezig(true);
    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      // Een prompt is eenmalig; hij is nu op.
      vergeetPrompt();
      if (outcome === "accepted") onthoudKlaar();
      else onthoudLater();
      setStand(null);
    } finally {
      setBezig(false);
    }
  }

  function later() {
    onthoudLater();
    setStand(null);
  }

  return (
    // Bewust geen role="dialog": dit venster houdt de aandacht niet vast en je
    // kunt eromheen gewoon doorlezen. Dat als dialoog aankondigen zou een
    // schermlezer iets beloven wat er niet is.
    <aside
      aria-labelledby="installeer-titel"
      className="fixed bottom-[calc(5.25rem+env(safe-area-inset-bottom))] left-1/2 z-[60] w-full max-w-md -translate-x-1/2 px-4 duration-300 animate-in fade-in slide-in-from-bottom-6"
    >
      <div className="relative overflow-hidden rounded-2xl border-2 border-primary bg-card shadow-[0_10px_40px_rgba(0,0,0,0.55)]">
        <button
          type="button"
          onClick={later}
          aria-label="Later"
          className="absolute right-2 top-2 flex size-8 items-center justify-center rounded-full text-white/50 transition-colors active:bg-white/10 active:text-white"
        >
          <X className="size-4" aria-hidden />
        </button>

        <div className="px-4 pb-4 pt-4">
          <p className="font-display text-xs font-bold uppercase tracking-widest text-primary">
            Tip
          </p>
          <h2
            id="installeer-titel"
            className="mt-1 pr-8 font-display text-lg font-black uppercase leading-tight tracking-tight text-white"
          >
            Zet NTTO op je beginscherm
          </h2>

          <Inhoud
            stand={stand}
            bezig={bezig}
            onInstalleer={installeer}
            onLater={later}
          />
        </div>
      </div>
    </aside>
  );
}

function Inhoud({
  stand,
  bezig,
  onInstalleer,
  onLater,
}: {
  stand: Stand;
  bezig: boolean;
  onInstalleer: () => void;
  onLater: () => void;
}) {
  // Android met een bruikbare prompt: de browser doet het werk, wij hoeven
  // alleen te vragen.
  if (stand.toestel === "android" && stand.metKnop) {
    return (
      <>
        <p className="mt-2 text-sm leading-relaxed text-soft-foreground">
          Dan staat hij tussen je andere apps, opent hij zonder browserbalk en werkt hij ook op
          een terrein zonder bereik.
        </p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onInstalleer}
            disabled={bezig}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 font-display text-sm font-black uppercase tracking-wide text-black transition-opacity active:opacity-80 disabled:opacity-60"
          >
            <ArrowDownToLine className="size-4" aria-hidden />
            {bezig ? "Bezig…" : "Toevoegen"}
          </button>
          <button
            type="button"
            onClick={onLater}
            className="rounded-full px-4 py-3 font-display text-sm font-bold uppercase tracking-wide text-white/60 transition-colors active:text-white"
          >
            Later
          </button>
        </div>
      </>
    );
  }

  // Android zonder prompt - een andere browser dan Chrome, of Chrome vindt het
  // nog te vroeg. Dan de weg wijzen in plaats van een dode knop tonen.
  if (stand.toestel === "android") {
    return (
      <Stappen onLater={onLater}>
        <Stap icoon={<EllipsisVertical className="size-4 shrink-0 text-primary" aria-hidden />}>
          Tik rechtsboven op de drie puntjes
        </Stap>
        <Stap icoon={<Plus className="size-4 shrink-0 text-primary" aria-hidden />}>
          Kies &ldquo;App installeren&rdquo; of &ldquo;Toevoegen aan startscherm&rdquo;
        </Stap>
      </Stappen>
    );
  }

  // iPhone en iPad. Apple heeft hier geen knop voor - dit is de enige weg, en
  // het is ook de enige manier waarop meldingen ooit gaan werken.
  if (stand.toestel === "ios-safari") {
    return (
      <Stappen
        onLater={onLater}
        naschrift="Meldingen over je teams werken op een iPhone alleen vanaf het beginscherm. Dat is een regel van Apple, niet iets wat wij aan kunnen zetten."
      >
        <Stap icoon={<Share className="size-4 shrink-0 text-primary" aria-hidden />}>
          Tik onderin op de deelknop
        </Stap>
        <Stap icoon={<Plus className="size-4 shrink-0 text-primary" aria-hidden />}>
          Kies &ldquo;Zet op beginscherm&rdquo;
        </Stap>
      </Stappen>
    );
  }

  // Chrome, Firefox of Edge op een iPhone. Die kunnen dit niet - op iOS mag
  // alleen Safari een app op het beginscherm zetten. Dat zeggen we eerlijk in
  // plaats van stappen te geven die doodlopen.
  return (
    <Stappen onLater={onLater}>
      <Stap icoon={<Compass className="size-4 shrink-0 text-primary" aria-hidden />}>
        Open deze pagina in Safari
      </Stap>
      <Stap icoon={<Share className="size-4 shrink-0 text-primary" aria-hidden />}>
        Daar staat onder de deelknop &ldquo;Zet op beginscherm&rdquo;
      </Stap>
    </Stappen>
  );
}

function Stappen({
  children,
  naschrift,
  onLater,
}: {
  children: React.ReactNode;
  naschrift?: string;
  onLater: () => void;
}) {
  return (
    <>
      <ol className="mt-3 space-y-2">{children}</ol>
      {naschrift && <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{naschrift}</p>}
      <button
        type="button"
        onClick={onLater}
        className="mt-3 font-display text-sm font-bold uppercase tracking-wide text-white/60 transition-colors active:text-white"
      >
        Later
      </button>
    </>
  );
}

function Stap({ icoon, children }: { icoon: React.ReactNode; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2 text-sm text-soft-foreground">
      {icoon}
      {children}
    </li>
  );
}
